import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";


import { handleGetNotifications } from "../../shared/controllers/notificationController";
import { unwrapApiArray, unwrapApiRecord } from "../../shared/utils/apiResponse";
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

interface NotificationMeta {
    current_page: number;
    last_page: number;
    [key: string]: unknown;
}

interface NotificationDataPayload {
    title?: string;
    description?: string;
    [key: string]: unknown;
}

export interface NotificationItem {
    id: string | number;
    data?: NotificationDataPayload;
    created_at?: string;
    read_at?: string | null;
    [key: string]: unknown;
}

interface NotificationsContextType {
    notifications: NotificationItem[];
    meta: NotificationMeta;
    loadingNotifications: boolean;
    unreadCount: number;
    refreshNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotificationsContext = () => {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error("useNotificationsContext must be used within a NotificationsProvider");
    }
    return context;
};

interface NotificationsProviderProps {
    children: React.ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [meta, setMeta] = useState<NotificationMeta>({ current_page: 1, last_page: 1 });
    const [loadingNotifications, setLoading] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);
    const inFlightRef = useRef(false);
    const lastFetchAtRef = useRef(0);

    const fetchNotifications = useCallback(async (page = 1) => {
        const now = Date.now();
        if (inFlightRef.current) {
            return;
        }

        // Guard against accidental rapid-fire calls from UI effects
        if (now - lastFetchAtRef.current < 800) {
            return;
        }

        inFlightRef.current = true;
        lastFetchAtRef.current = now;
        setLoading(true);
        try {
            const params = {
                per_page: 5,
                page,
            };
            const data = await handleGetNotifications<Record<string, unknown>>("user", params);
            const rawNotificationList = unwrapApiArray<Record<string, unknown>>(data);
            const notificationList: NotificationItem[] = [];
            rawNotificationList.forEach((item) => {
                const itemId = item.id;
                if (typeof itemId !== "string" && typeof itemId !== "number") {
                    return;
                }

                const payload = isRecord(item.data) ? item.data : {};
                notificationList.push({
                    ...item,
                    id: itemId,
                    data: payload as NotificationDataPayload,
                    ...(typeof item.created_at === "string"
                        ? { created_at: item.created_at }
                        : {}),
                    ...((typeof item.read_at === "string" || item.read_at === null)
                        ? { read_at: item.read_at }
                        : {}),
                });
            });

            if (notificationList.length) {
                setNotifications(notificationList);

                const payload = unwrapApiRecord<Record<string, unknown>>(data);
                const metaPayload = (payload.meta as Record<string, unknown> | undefined) ?? {};
                setMeta({
                    current_page: Number(metaPayload.current_page ?? 1),
                    last_page: Number(metaPayload.last_page ?? 1),
                });
                setUnreadCount(notificationList.filter((n) => n.read_at === null).length);
            } else {
                setNotifications([]);
                setMeta({ current_page: 1, last_page: 1 });
                setUnreadCount(0);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setNotifications([]);
            setMeta({ current_page: 1, last_page: 1 });
            setUnreadCount(0);
        } finally {
            setLoading(false);
            inFlightRef.current = false;
        }
    }, []);

    const refreshNotifications = useCallback(() => {
        void fetchNotifications();
    }, [fetchNotifications]);

    useEffect(() => {
        void fetchNotifications();
    }, [fetchNotifications]);

    const contextValue = useMemo(
        () => ({
            notifications,
            meta,
            loadingNotifications,
            unreadCount,
            refreshNotifications,
        }),
        [notifications, meta, loadingNotifications, unreadCount, refreshNotifications]
    );

    return (
        <NotificationsContext.Provider value={contextValue}>
            {children}
        </NotificationsContext.Provider>
    );
};
